import { extractApiErrorMessage } from "@/lib/api/errors";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SignupStep } from "./SignupStep";
import { EmailVerification } from "./EmailVerification";
import { User, Phone, Mail } from "lucide-react";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { countryPhoneSchema } from "@/lib/validation/phone";
import { fullNameSchema } from "@/lib/validation/fullName";
import { cn } from "@/lib/utils";
import { isSameEmail } from "@/lib/verificationCode";

const basicInfoSchema = z.object({
  // `full_name` is what the backend turns into the applicant's permanent
  // username, so it is normalised and screened for self-repetition here —
  // the last point before it leaves the browser (#873).
  fullName: fullNameSchema,
  email: z
    .email("Please enter a valid email address")
    .max(255, "Email must not exceed 255 characters"),
  phone: countryPhoneSchema,
});

type BasicInfoFormData = z.infer<typeof basicInfoSchema>;

interface BasicInfoStepProps {
  formData: {
    fullName: string;
    email: string;
    phone: string;
  };
  emailVerified: boolean;
  isLoading: boolean;
  error?: string;
  hasActiveSession: boolean; // Track if application has been started
  /**
   * The address the active session's code was sent to. `formData.email`
   * follows the input as it is edited, so this is what tells an unchanged
   * address (go back to the code screen) from a new one (start again and
   * send a new code) — backend#832.
   */
  verificationEmail?: string | null;
  /** Minutes the latest code stays valid, from the send-code response. */
  codeExpiresInMinutes?: number | null;
  onFormChange: (data: Partial<BasicInfoFormData>) => void;
  onStartApplication: (data: BasicInfoFormData) => Promise<void>;
  onVerifyEmail: (code: string) => Promise<void>;
  onResendCode: () => Promise<void>;
  onNext: () => void;
  onBack: () => void;
}

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="text-sm text-destructive font-medium">{message}</p> : null;

/** Label of the basic-info form's primary button. */
const primaryButtonText = (emailVerified: boolean, isReturningToCode: boolean): string => {
  if (emailVerified) {
    return "Continue";
  }
  return isReturningToCode ? "Back to verification" : "Send Verification Code";
};

const infoMessage = (isChangingSessionEmail: boolean): string =>
  isChangingSessionEmail
    ? "Only the email address can be changed here. A new address gets a new verification code."
    : "We'll send a verification code to your email address. Please make sure it's correct.";

interface CodeScreenOptions {
  hasActiveSession: boolean;
  verificationEmail?: string | null;
  /** Address currently in the wizard state (follows the input). */
  email: string;
  emailVerified: boolean;
  onStartApplication: (data: BasicInfoFormData) => Promise<void>;
  onVerifyEmail: (code: string) => Promise<void>;
  onResendCode: () => Promise<void>;
  onNext: () => void;
}

/** Which of the step's two screens is showing, and the actions that move between them. */
const useCodeScreen = ({
  hasActiveSession,
  verificationEmail,
  email,
  emailVerified,
  onStartApplication,
  onVerifyEmail,
  onResendCode,
  onNext,
}: CodeScreenOptions) => {
  const [showVerification, setShowVerification] = useState(false);
  // Set when the applicant leaves the code screen to change the address. An
  // active unverified session would otherwise force the code screen straight
  // back, leaving no way to correct a typo or swap a slow mailbox (#832).
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [verificationError, setVerificationError] = useState<string | undefined>();

  const onSubmit = async (data: BasicInfoFormData) => {
    // Unchanged address with a live session: its code is already on the way
    // (and the backend refuses a second application for the same address), so
    // go back to the code screen instead of starting again.
    if (hasActiveSession && isSameEmail(data.email, verificationEmail)) {
      setVerificationError(undefined);
      setIsEditingEmail(false);
      setShowVerification(true);
      return;
    }
    try {
      setVerificationError(undefined);
      await onStartApplication(data);
      setIsEditingEmail(false);
      setShowVerification(true);
    } catch (err) {
      // Error handling is done in parent component
      console.error("Failed to start application:", err);
    }
  };

  const handleEmailVerification = async (code: string) => {
    try {
      setVerificationError(undefined);
      await onVerifyEmail(code);
      setShowVerification(false);
      // Auto-advance to next step after successful verification
      onNext();
    } catch (err) {
      setVerificationError(extractApiErrorMessage(err, "Invalid verification code"));
    }
  };

  const handleResendCode = async () => {
    try {
      setVerificationError(undefined);
      await onResendCode();
    } catch (err) {
      setVerificationError(extractApiErrorMessage(err, "Failed to resend code"));
      // Re-throw so the code screen does not announce a "new code sent" or
      // start the cooldown for a resend that failed.
      throw err;
    }
  };

  const handleChangeEmail = () => {
    setVerificationError(undefined);
    setShowVerification(false);
    setIsEditingEmail(true);
  };

  // Show the code screen if the applicant just sent a code, or has an active
  // session that is not verified yet — unless they asked to change the address.
  const awaitingCode = hasActiveSession && !!email && !emailVerified;
  const showCodeScreen = !isEditingEmail && (showVerification || awaitingCode);

  return {
    showCodeScreen,
    isEditingEmail,
    verificationError,
    onSubmit,
    handleEmailVerification,
    handleResendCode,
    handleChangeEmail,
  };
};

export const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  formData,
  emailVerified,
  isLoading,
  error,
  hasActiveSession,
  verificationEmail,
  codeExpiresInMinutes,
  onFormChange,
  onStartApplication,
  onVerifyEmail,
  onResendCode,
  onNext,
  onBack,
}) => {
  const {
    showCodeScreen,
    isEditingEmail,
    verificationError,
    onSubmit,
    handleEmailVerification,
    handleResendCode,
    handleChangeEmail,
  } = useCodeScreen({
    hasActiveSession,
    verificationEmail,
    email: formData.email,
    emailVerified,
    onStartApplication,
    onVerifyEmail,
    onResendCode,
    onNext,
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
  } = useForm<BasicInfoFormData>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: formData,
    mode: "onChange",
  });

  // Watch form changes and update parent state
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch() is not React Compiler-compatible
    const subscription = watch(value => {
      onFormChange(value as Partial<BasicInfoFormData>);
    });
    return () => subscription.unsubscribe();
  }, [watch, onFormChange]);

  if (showCodeScreen) {
    const codeSentTo = verificationEmail || formData.email;
    return (
      <SignupStep
        title="Verify Your Email"
        description="Please verify your email address to continue with your consultant application."
        canGoNext={emailVerified}
        canGoBack={true}
        isLoading={isLoading}
        nextButtonText="Continue"
        backButtonText="Change email"
        onNext={onNext}
        onBack={handleChangeEmail}
      >
        {/* Keyed by address: a new address is a new code, so the resend
            cooldown and the slow-mail timer start over. */}
        <EmailVerification
          key={codeSentTo}
          email={codeSentTo}
          onVerify={handleEmailVerification}
          onResendCode={handleResendCode}
          onChangeEmail={handleChangeEmail}
          expiresInMinutes={codeExpiresInMinutes}
          isLoading={isLoading}
          error={verificationError}
        />
      </SignupStep>
    );
  }

  const isReturningToCode =
    !emailVerified && hasActiveSession && isSameEmail(watch("email"), verificationEmail);
  // Name and phone are saved only when an application starts, so in
  // change-email mode they are locked: an edit there could not reach the
  // backend when the address stays the same.
  const lockNameAndPhone = isEditingEmail && hasActiveSession;

  return (
    <SignupStep
      title="Basic Information"
      description="Let's start with your basic information to create your consultant profile."
      canGoNext={isValid}
      canGoBack={false}
      isFirstStep={true}
      isLoading={isLoading}
      nextButtonText={primaryButtonText(emailVerified, isReturningToCode)}
      onNext={emailVerified ? onNext : handleSubmit(onSubmit)}
      onBack={onBack}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Full Name Field */}
        <div className="space-y-2">
          <Label htmlFor="fullName" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Full Name *
          </Label>
          <Input
            id="fullName"
            type="text"
            placeholder="Enter your full name"
            {...register("fullName")}
            className={cn(
              "transition-all duration-200",
              errors.fullName && "border-destructive focus-visible:ring-destructive",
              "read-only:bg-muted/50 read-only:text-muted-foreground"
            )}
            disabled={isLoading}
            // readOnly rather than disabled: RHF drops disabled inputs' values.
            readOnly={lockNameAndPhone}
          />
          <FieldError message={errors.fullName?.message} />
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <Label htmlFor="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email Address *
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email address"
            {...register("email")}
            className={cn(
              "transition-all duration-200",
              errors.email && "border-destructive focus-visible:ring-destructive",
              emailVerified && "border-green-500 bg-green-50"
            )}
            disabled={isLoading}
          />
          <FieldError message={errors.email?.message} />
          {emailVerified && (
            <p className="text-sm text-green-600 font-medium">✓ Email verified successfully</p>
          )}
        </div>

        {/* Phone Field */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Phone Number *
          </Label>
          <PhoneInput
            id="phone"
            value={watch("phone") ?? ""}
            onChange={val => setValue("phone", val, { shouldValidate: true })}
            hasError={!!errors.phone}
            disabled={isLoading || lockNameAndPhone}
          />
          <FieldError message={errors.phone?.message} />
          <p className="text-xs text-muted-foreground">
            Select your country, then enter your number
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Info Message */}
        <Alert>
          <AlertDescription>{infoMessage(lockNameAndPhone)}</AlertDescription>
        </Alert>
      </form>
    </SignupStep>
  );
};
